// One SDK subscription per client/topic/filter, with independent React owners.
// Realtime 2.11.10 reuses channel names and only permits subscribe() once.
const clients = new WeakMap();
let generation = 0;
const unavailable = () => console.warn('Live updates unavailable; refresh to load current data.');

export function subscribeToChanges(client, topic, filter, listener) {
  let subscriptions = clients.get(client);
  if (!subscriptions) clients.set(client, subscriptions = new Map());
  const key = JSON.stringify([topic, filter.event, filter.schema, filter.table, filter.filter ?? null]);
  let entry = subscriptions.get(key);
  if (!entry) {
    // A retiring channel can still await its leave acknowledgement. Never reuse
    // that instance for a remount, even before asynchronous cleanup completes.
    entry = { listeners: new Set(), channel: client.channel(`${topic}:owner-${++generation}`) };
    subscriptions.set(key, entry);
    entry.channel.on('postgres_changes', filter, payload => {
      for (const owner of [...entry.listeners]) {
        try { Promise.resolve(owner(payload)).catch(unavailable); } catch { unavailable(); }
      }
    });
  }
  // Each acquisition owns a separate callback, even if callers share a function.
  const owner = payload => listener(payload);
  entry.listeners.add(owner);
  if (entry.listeners.size === 1) {
    try { entry.channel.subscribe(); }
    catch (error) {
      subscriptions.delete(key);
      entry.listeners.clear();
      void close(entry.channel);
      unavailable();
      return () => {};
    }
  }
  return () => {
    if (!entry.listeners.delete(owner) || entry.listeners.size) return;
    subscriptions.delete(key);
    void close(entry.channel);
  };
}

async function close(channel) {
  try {
    // unsubscribe's close event removes this topic from the SDK registry. Avoid
    // removeChannel in 2.11.10: its post-await filter compares method references
    // and can also remove unrelated active channels from that registry.
    const status = await channel.unsubscribe();
    if (status === 'error') unavailable();
  } catch { unavailable(); }
}
